import { InputHTMLAttributes, ReactNode, useState } from "react";
import Button from "../common/Button";
import Input from "../common/Input";

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string | ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  action?: string;
  id?: string;
  placeholder?: string;
  onSave?: (value: string) => void;
}

export default function TextInput({
  label,
  size = "md",
  action,
  id,
  placeholder,
  value = "",
  onSave,
  ...props
}: TextInputProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value as string);
  const [tempValue, setTempValue] = useState(value as string);
  const uniqueId = id || `input-${typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, "-") : 'custom'}`;

  function getTextInputSizes() {
    switch (size) {
      case "xs":
        return {
          button: "text-xs px-1 max-w-24",
          label: "text-xs",
        };
      case "sm":
        return {
          button: "text-xs md:text-sm px-1 md:px-4 max-w-32 md:max-w-fit",
          label: "text-xs md:text-base",
        };
      case "lg":
        return {
          button: "text-base px-6 max-w-fit",
          label: "text-lg",
        };
      case "md":
      default:
        return {
          button: "text-sm px-4 max-w-fit",
          label: "text-base",
        };
    }
  }

  const sizeClasses = getTextInputSizes();

  const handleSave = () => {
    setInputValue(tempValue);
    if (onSave) {
      onSave(tempValue);
    }
    setIsModalOpen(false);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setTempValue(inputValue);
  };

  return (
    <fieldset className="form-control w-full">
      <div className="label p-0 min-h-10">
        <span className="flex items-center">
          <span className={`text-gray-800 dark:text-base-content/80 font-medium ${sizeClasses.label}`}>
            {label}
          </span>
        </span>
        <Button
          variant="outline"
          size={size}
          onClick={() => setIsModalOpen(true)}
          className={sizeClasses.button}
        >
          {inputValue || action || label}
        </Button>
      </div>

      <dialog
        id={`modal-${uniqueId}`}
        className={`modal ${isModalOpen ? "modal-open" : ""}`}
      >
        <div className="modal-box max-w-96">
          <h3 className="font-bold text-lg mt-2">
            {action || label}
          </h3>
          <div className="py-1">
            <Input
              type="text"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={placeholder}
              className="!w-full !max-w-full"
            />
          </div>
          <div className="modal-action mt-2">
            <Button variant="outline" onClick={resetModal}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary">
              Update
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={resetModal}>close</button>
        </form>
      </dialog>
    </fieldset>
  );
}
